import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  ClipboardList,
  Cloud,
  ListOrdered,
  Mail,
  Share2,
  ShieldCheck,
  Trophy,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

import LapstrTextLogo from "@/assets/Lapstr-text.png";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { isNativeApp } from "@/lib/runtime";

const nativeFeatures = [
  {
    icon: ClipboardList,
    title: "Digitalt matchprotokoll",
    description: "Skapa och hantera matchprotokoll direkt i mobilen.",
  },
  {
    icon: Trophy,
    title: "Matcher och resultat",
    description: "Följ matcher, resultat och säsongens utveckling.",
  },
  {
    icon: Users,
    title: "Tabell, lag och förare",
    description: "Samlad överblick över tabell, trupper och förarstatistik.",
  },
];

const nativeCapabilities = [
  [ClipboardList, "Digitalt protokoll", "Snabbt och säkert"],
  [Share2, "Dela och exportera", "PDF och delningslänk"],
  [Trophy, "Matcher och resultat", "Aktuellt och historik"],
  [Cloud, "Molnlagring", "Tillgängligt på dina enheter"],
  [BarChart3, "Tabell och statistik", "Aktuell ställning"],
  [ShieldCheck, "Byggt för ligan", "Anpassat för speedway"],
];

const webFeatures = [
  {
    icon: ClipboardList,
    title: "Digitala matchprotokoll",
    description: "För protokoll digitalt i appen i stället för traditionellt med papper och penna.",
  },
  {
    icon: ListOrdered,
    title: "Laguppställningar",
    description: "Följ publicerade laguppställningar och få en tydlig överblick inför matchstart.",
  },
  {
    icon: Trophy,
    title: "Matcher och tabell",
    description: "Håll koll på matcher, resultat och tabelläget direkt i appen.",
  },
];

function NativeAboutPublicPage() {
  return (
    <div className="native-v3-about-public">
      <header className="native-v3-about-header">
        <Link to="/auth" className="native-v3-about-brand" aria-label="Till inloggningen">
          <img src="/icons/icon-512x512.png" alt="" aria-hidden="true" />
          <img src={LapstrTextLogo} alt="Lapstr" />
        </Link>
        <div className="native-v3-about-header-actions">
          <Link to="/auth">Logga in</Link>
          <Link to="/auth" state={{ authMode: "register" }}>Kom igång</Link>
        </div>
      </header>

      <main>
        <section className="native-v3-about-hero">
          <p className="native-v3-auth-kicker">DIGITAL SPEEDWAY</p>
          <h1 className="native-v3-about-display">Speedway.<br />Enklare.</h1>
          <p className="native-v3-about-lead">
            Digitalt matchprotokoll, matcher, tabell, lag och förare för svensk speedway.
          </p>
          <Link className="native-v3-about-primary" to="/auth" state={{ authMode: "register" }}>
            Skapa konto <ArrowRight aria-hidden="true" />
          </Link>
          <Link className="native-v3-about-login" to="/auth">Logga in</Link>
          <div className="native-v3-about-proof">
            <span><strong>15</strong> heat</span>
            <span><strong>8</strong> lag</span>
            <span><strong>En app</strong> samlad upplevelse</span>
          </div>
        </section>

        <section className="native-v3-about-section">
          <p className="native-v3-auth-kicker">VARFÖR LAPSTR</p>
          <h2 className="native-v3-about-section-title">Allt kring matchen.</h2>
          <div className="native-v3-about-feature-list">
            {nativeFeatures.map(({ icon: Icon, title, description }) => (
              <div className="native-v3-about-feature" key={title}>
                <Icon aria-hidden="true" />
                <div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="native-v3-about-stat-strip">
            <span><strong>15</strong> heat per match</span>
            <span><strong>8</strong> lag i serien</span>
            <span><strong>1</strong> samlad upplevelse</span>
          </div>
        </section>

        <section className="native-v3-about-section">
          <p className="native-v3-auth-kicker">SÅ FUNGERAR DET</p>
          <h2 className="native-v3-about-section-title">Från match till protokoll.</h2>
          <ol className="native-v3-about-steps">
            <li><span>1</span><div><strong>Välj match</strong><p>Välj den officiella match du vill registrera.</p></div></li>
            <li><span>2</span><div><strong>Fyll i heat</strong><p>Registrera heatresultaten direkt i appen.</p></div></li>
            <li><span>3</span><div><strong>Spara protokollet</strong><p>Spara och öppna protokollet när du behöver det.</p></div></li>
          </ol>
        </section>

        <section className="native-v3-about-section">
          <p className="native-v3-auth-kicker">OM TJÄNSTEN</p>
          <h2 className="native-v3-about-section-title">Skapad för speedway.</h2>
          <p className="native-v3-about-editorial">
            Lapstr är en fristående digital tjänst för dig som följer svensk speedway. Här
            samlas matchprotokoll, matcher, resultat, tabell, lag och förare i ett tydligt flöde.
          </p>
          <h3 className="native-v3-about-subtitle">Det här hittar du i Lapstr</h3>
          <div className="native-v3-about-capabilities">
            {nativeCapabilities.map(([Icon, title, description]) => (
              <div key={title}>
                <Icon aria-hidden="true" />
                <span><strong>{title}</strong><small>{description}</small></span>
              </div>
            ))}
          </div>
        </section>

        <section className="native-v3-about-contact">
          <p className="native-v3-auth-kicker">KONTAKT & SUPPORT</p>
          <a href="mailto:support@lapstr.se"><Mail aria-hidden="true" /> support@lapstr.se <ArrowRight aria-hidden="true" /></a>
          <Link className="native-v3-about-primary" to="/auth" state={{ authMode: "register" }}>
            Börja använda Lapstr <ArrowRight aria-hidden="true" />
          </Link>
        </section>
      </main>

      <footer className="native-v3-about-footer">
        <div><Link to="/legal#privacy">Integritetspolicy</Link><Link to="/legal#terms">Användarvillkor</Link></div>
      </footer>
    </div>
  );
}

function WebAboutPublicPage() {
  return (
    <div className="min-h-screen bg-background px-6 py-10">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
        <div className="flex items-center justify-between gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/auth" className="flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" /> Till inloggning
            </Link>
          </Button>
        </div>

        <Card className="overflow-hidden">
          <CardContent className="grid gap-8 p-6">
            <div className="grid gap-5">
              <img src={LapstrTextLogo} alt="Lapstr" className="h-auto w-[clamp(160px,34vw,320px)]" />
              <div className="grid gap-3">
                <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Om Lapstr</h1>
                <p className="max-w-3xl text-base text-muted-foreground sm:text-lg">
                  Lapstr är appen för dig som vill arbeta med digitala matchprotokoll i speedway
                  och samtidigt följa matcher, laguppställningar och tabell på ett ställe.
                </p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {webFeatures.map(({ icon: Icon, title, description }) => (
                <div className="rounded-2xl border border-border/70 bg-muted/20 p-5" key={title}>
                  <Icon className="mb-3 h-5 w-5 text-primary" />
                  <h2 className="mb-2 font-semibold text-foreground">{title}</h2>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              ))}
            </div>

            <div className="grid gap-4 rounded-2xl border border-border/70 bg-card p-6">
              <CardHeader className="p-0"><CardTitle className="text-xl">Speedway och ett tydligare arbetsflöde</CardTitle></CardHeader>
              <CardContent className="grid gap-4 p-0 text-sm text-muted-foreground">
                <p>Lapstr är framtaget för att kombinera matchöversikt, tabell och laguppställningar med ett tydligt flöde för digitala matchprotokoll.</p>
                <p>Målet är att göra det enklare att följa matchdagen, arbeta i protokollet och samla relevant speedwayinformation i ett mer lättillgängligt format.</p>
                <p>För dig som använder digitala matchprotokoll blir Lapstr ett verktyg som samlar både protokollarbetet och överblicken över matcher på samma plats.</p>
                <p>Sidan finns publikt för att göra det enklare att förstå vad Lapstr är, medan själva appfunktionerna ligger bakom inloggning.</p>
              </CardContent>
            </div>

            <div className="flex flex-col items-start gap-3 rounded-2xl border border-primary/20 bg-primary/5 p-6">
              <h2 className="text-lg font-semibold text-foreground">Glöm inte att registrera dig</h2>
              <p className="text-sm text-muted-foreground">Skapa konto för att komma igång med Lapstr och använda appens digitala matchprotokoll för speedway.</p>
              <Button asChild><Link to="/auth">Registrera dig här</Link></Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function AboutPublicPage() {
  const native = isNativeApp();
  return (
    <>
      <Seo
        title="Om Lapstr"
        description="Läs mer om Lapstr, appen för digitala matchprotokoll i speedway, samt hur tjänsten samlar matcher, laguppställningar och tabell."
        canonicalPath="/om-lapstr"
      />
      {native ? <NativeAboutPublicPage /> : <WebAboutPublicPage />}
    </>
  );
}
